import { useRef } from "react";
import WidgetPlaceholder from "./WidgetPlaceholder";
import KpiComparison, { type KpiComparisonData } from "./KpiComparison";
import KpiExportMenu from "./KpiExportMenu";
import WidgetExportCard from "./WidgetExportCard";
import type { KpiExportData } from "src/helpers/widgetExport";

// Rounds any decimal number to at most 1 decimal place (never adds one to a
// whole number) and re-applies en-US thousand separators. Handles both a
// bare number (23.4567 → "23.5") and a pre-formatted API string with a
// suffix (e.g. "45.6789%" → "45.7%", "1,234.567 days" → "1,234.6 days").
const formatKpiValue = (val: number | string | undefined | null): string => {
  if (val === undefined || val === null || val === "") return "0";

  if (typeof val === "number") {
    return (Math.round(val * 10) / 10).toLocaleString("en-US", {
      maximumFractionDigits: 1,
    });
  }

  return val.replace(/-?[\d,]*\.\d+/g, (match) => {
    const n = parseFloat(match.replace(/,/g, ""));
    if (Number.isNaN(n)) return match;
    return (Math.round(n * 10) / 10).toLocaleString("en-US", {
      maximumFractionDigits: 1,
    });
  });
};

type KpiData = {
  total_count?: number | string;
  label?: string;
  subtitle?: string;
  caption?: string;
  formula?: string;
  comparison?: KpiComparisonData | null;
};

type KpiWidgetProps = {
  isLoading: boolean;
  data: unknown;
  title?: string;
  description?: string | null;
  widgetId?: string | number;
  // Rendered off-screen at a fixed size for image/PDF export: no export menu
  // (avoids recursion), no flex-fill dependence on the live tile.
  exportMode?: boolean;
};

export default function KpiWidget({
  isLoading,
  data,
  title,
  description,
  widgetId,
  exportMode,
}: KpiWidgetProps) {
  const hostRef = useRef<HTMLDivElement>(null);

  if (isLoading || data === undefined || data === null) {
    return <WidgetPlaceholder height={48} />;
  }

  const kpiData = data as KpiData;

  const body = (
    <div className="kpi-counter-widget d-flex flex-column flex-fill align-items-center justify-content-center text-center">
      <div>
        <p className="kpi-counter-widget__value mb-2">
          {formatKpiValue(kpiData.total_count ?? 0)}
        </p>
        {kpiData.subtitle && (
          <p
            className="mb-1"
            style={{ fontSize: 11.5, color: "var(--bs-secondary-color)" }}
          >
            {kpiData.subtitle}
          </p>
        )}
        {kpiData.caption && (
          <p
            className="mb-1"
            style={{ fontSize: 11.5, color: "var(--bs-secondary-color)" }}
          >
            {kpiData.caption}
          </p>
        )}
        <KpiComparison comparison={kpiData.comparison} />
      </div>
    </div>
  );

  if (exportMode) {
    return (
      <div style={{ minHeight: 150, display: "flex", flexDirection: "column" }}>
        {body}
      </div>
    );
  }

  return (
    <div
      ref={hostRef}
      className="d-flex flex-column flex-fill"
      style={{ minHeight: 0 }}
    >
      <KpiExportMenu
        data={kpiData as KpiExportData}
        title={title}
        description={description}
        targetRef={hostRef}
        menuId={widgetId !== undefined ? String(widgetId) : undefined}
        renderExport={() => (
          <WidgetExportCard title={title}>
            <KpiWidget isLoading={false} data={data} exportMode />
          </WidgetExportCard>
        )}
      />
      {body}
    </div>
  );
}
